import { type Ref, computed } from "vue";

import { getMissingOfferTemplates } from "@/helpers/offers";

import type { IFunder } from "@/models/funders";
import type { OfferTemplate, OfferTemplateBase } from "@/models/offers";
import type { PlacementPresetsMap, RowState } from "@/models/placement";
import type { IScorecardResult } from "@/models/scorecards";

export const useFunderTableData = (
  funder: IFunder,
  templates: Ref<OfferTemplate[] | undefined>,
  rowStates: Ref<Record<string, RowState>>,
  funderPlacementPresetsMap?: Ref<PlacementPresetsMap>,
  scorecardResults?: Ref<IScorecardResult[]>
) => {
  const getPlacementPresets = (templateId: string) => {
    if (!funderPlacementPresetsMap?.value) {
      return {
        workflow_template_id: funder.workflow_template_id,
        client_offer_template: funder.client_offer_template,
      };
    }

    const placementPreset =
      funderPlacementPresetsMap.value[funder.id]?.[templateId];
    if (!placementPreset) {
      return {
        workflow_template_id: funder.workflow_template_id,
        client_offer_template: funder.client_offer_template,
      };
    }

    return {
      workflow_template_id:
        placementPreset.workflow_template ?? funder.workflow_template_id,
      client_offer_template:
        placementPreset.offer_template ?? funder.client_offer_template,
    };
  };

  const getScorecardResults = (templateId: string): IScorecardResult[] => {
    if (!scorecardResults?.value?.length) {
      return [];
    }

    const filteredResults = scorecardResults.value.filter((sc) => {
      const scorecardFunderId = (sc.score_card?.funder as IFunder)?.id;
      return (
        scorecardFunderId === funder.id &&
        sc.score_card?.origin_offer_template_id === templateId
      );
    });

    return filteredResults.sort((a, b) => {
      const scoreA = Number(a.points ?? -1) / (a.score_card?.points ?? 1);
      const scoreB = Number(b.points ?? -1) / (b.score_card?.points ?? 1);
      return scoreB - scoreA;
    });
  };

  const createRowData = (template: OfferTemplate | OfferTemplateBase) => {
    const rowId = `${funder.id}_${template.id}`;
    const presets = getPlacementPresets(template.id);
    const rowState = rowStates.value[rowId];

    return {
      ...template,
      id: rowId,
      original_template_id: template.id,
      funder_id: funder.id,
      full_name: funder.full_name,
      client_id: funder.client_id,
      offer_template: template,
      client_offer_template:
        rowState?.client_offer_template ?? presets.client_offer_template,
      workflow_template_id:
        rowState?.workflow_template_id ?? presets.workflow_template_id,
      selected_method: rowState?.selected_method ?? funder.selected_method,
      risk_tiers: funder.risk_tiers,
      score_card_results: getScorecardResults(template.id),
      available_methods: funder.available_methods,
    };
  };

  return computed(() => {
    const funderProducts = funder.product_types ?? [];

    if (!templates.value) {
      return getMissingOfferTemplates([], {
        product_types: funderProducts,
      }).map(createRowData);
    }

    const customTemplates = templates.value.filter(
      (template) => !template.is_fallback
    );
    const missingTemplates = getMissingOfferTemplates(customTemplates, {
      product_types: funderProducts,
    });

    return [...customTemplates, ...missingTemplates].map(createRowData);
  });
};
